/* eslint-disable camelcase */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useRef, useState } from "react";
import MaterialTable, { Column } from "@material-table/core";
import { Card, Grid } from "@material-ui/core";
import { useLazyQuery } from "@apollo/client";
import { Viewer } from "components/auth/types";
import { ApplicantSearch, ApplicantSearchVariables, ApplicantSearch_applicantSearch_items as SearchResultItem } from "types/gql-types";
import InterestListSelector from "../../interests-sidebar/interest-list-selector";
import { APPLICANT_SEARCH_QUERY } from "../query";
import CustomPagination from "./pagination-footer";
import SidebarFilters from "./elastic-sidebar";

type Props = {viewer: Viewer, interestListId: string, pageString?: string}

const columns: Array<Column<SearchResultItem>> = [
  { title: "Pos", field: "positionNumber" },
  { title: "Star", field: "hasStar" },
  { title: "First Name", field: "householdMember0FirstName" },
  { title: "Last Name", field: "householdMember0LastName" },
  { title: "Status", field: "status" },
  { title: "Phone Number", field: "applicantPhone" },
  // { title: "Family Size", field: "overageQuantity" },
  // { title: "Unit", field: "overageQuantity" },
  // { title: "Income", field: "overageQuantity" },
  // { title: "Received", field: "overageQuantity" },
];

const LayoutContainer: React.FC = ({ children }) => (
  <Grid container spacing={2} style={{ margin: 0, width: "100%" }} className="legacy-bs-override">
    {children}
  </Grid>
);

// const INACTIVE_STAGES = ["Removed", "Moved In"];
const getPageFromString = (str: string | undefined): number => {
  if (!str) return 0;
  const page = parseInt(str, 10);
  return Number.isNaN(page) ? 0 : page;
};

const ApplicantSearchComponent: React.FC<Props> = ({ viewer, interestListId, pageString }) => {
  const [hasPositionNumber, setHasPositionNumber] = useState(false);
  const [unitSize1br, setUnitSize1br] = useState(false);
  const [hasUserId, setHasUserId] = useState(false);
  const [currentStages, setCurrentStages] = useState<string[]>([]);

  const [page, setPage] = useState(getPageFromString(pageString));
  const [pageSize, setPageSize] = useState(25);
  const [textFilter, setTextFilter] = useState("");
  const [defaultSorting, setDefaultSorting] = useState<string | null>(null);

  const [filters, setFilters] = useState([]);
  const tableRef = useRef();

  // Use useSidebarFilters from FilterManager instead:

  const buildFilters = () => [];
  // const newFilters = {} as Record<string, NoRangeFilterData>;
  // const statuses = [] as string[];
  // if (hasPositionNumber) newFilters.has_position_number = { value: true, type: "boolean" };
  // if (unitSize1br) newFilters.unit_size_1br = { value: true, type: "boolean" };
  // if (hasUserId) newFilters.has_user_id = { value: true, type: "boolean" };

  // // Status filters
  // if (currentStages.includes("Active") &&
  // INACTIVE_STAGES.every(stage => !currentStages.includes(stage))) {
  //   newFilters.is_active = { value: true, type: "boolean" };
  // } else {
  //   currentStages.forEach(stage => statuses.push(stage));
  // }
  // if (statuses.length) newFilters.status = { value: statuses, type: "array" };


  // return Object.entries(newFilters).map(([key, filterData]) => ({
  //   ...filterData,
  //   field: key,
  // }));

  const [applicantSearch] =
  useLazyQuery<ApplicantSearch, ApplicantSearchVariables>(APPLICANT_SEARCH_QUERY, {
    variables: {
      elasticQuery: {
        filters: buildFilters(),
        textFilter,
        defaultSorting,
      },
      interestListIds: [interestListId],
      // Initial values for page, pageSize and textFilter, updated dynamically later
      page,
      pageSize,
    },
    fetchPolicy: "no-cache", // To ensure fresh data on every query
  });

  useEffect(() => {
    setPage(0);
    const newFilters = buildFilters();
    setFilters(newFilters);
    // Trigger the applicantSearch query with updated filters and reset page
    applicantSearch({
      variables: {
        interestListIds: [interestListId],
        elasticQuery: {
          filters: newFilters,
          textFilter,
          defaultSorting,
        },
        page: 0, // reset to the first page
        pageSize,
      },
    });
    // @ts-ignore
    if (tableRef && tableRef.current) tableRef.current.onQueryChange();
  }, [currentStages, hasPositionNumber, unitSize1br, hasUserId, pageSize, textFilter]);
  // Include textFilter in the dependency array


  const onListChange = () => null;

  return (
    <div style={{ padding: "0.5rem", width: "100%" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <Card>
            <InterestListSelector
              viewer={viewer}
              onListChange={onListChange}
              interestListId={interestListId}
            />

            <SidebarFilters
              setDefaultSorting={setDefaultSorting}
              interestListId={interestListId}
              currentStages={currentStages}
              setCurrentStages={setCurrentStages}
            />
            <form>
              <input
                type="checkbox"
                checked={hasPositionNumber}
                onChange={e => setHasPositionNumber(e.target.checked)}
              />
              <label>Has Position Number</label>

              <input
                type="checkbox"
                checked={unitSize1br}
                onChange={e => setUnitSize1br(e.target.checked)}
              />
              <label>Unit Size 1BR</label>

              <input
                type="checkbox"
                checked={hasUserId}
                onChange={e => setHasUserId(e.target.checked)}
              />
              <label>Has User ID</label>
            </form>
          </Card>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          {<MaterialTable
            tableRef={tableRef}
            title="Applications"
            style={{ padding: "1.5em" }}
            columns={columns}
            page={page}
            onPageChange={(currentPage) => setPage(currentPage)}
            onRowsPerPageChange={newPageSize => setPageSize(newPageSize)}
            onSearchChange={newTextFilter => setTextFilter(newTextFilter)}
            data={async queryData => {
              const result = await applicantSearch({
                variables: {
                  interestListIds: [interestListId],
                  elasticQuery: {
                    filters,
                    textFilter,
                  },
                  page,
                  pageSize,
                },
              });
              const totalRecords = result.data?.applicantSearch.hits || 0;
              const maxPage = Math.ceil(totalRecords / queryData.pageSize) - 1;
              const goToPage = Math.min(queryData.page, maxPage);
              return Promise.resolve({
                data: result.data?.applicantSearch.items || [],
                page: goToPage,
                totalCount: result.data?.applicantSearch.hits || 0,
              });
            }
            }
            options={{
              paginationType: "stepped",
              sorting: false,
              pageSize,
              columnsButton: true,
              selection: true,
              pageSizeOptions: [10, 25, 50],
            }}
            components={{
              Pagination: CustomPagination,
            }}
          />}

        </Grid>
      </Grid>
    </div>
  );
};

const InterestsPage: React.FC<Props> = ({ viewer, interestListId, pageString }) => (
  <LayoutContainer>
    <ApplicantSearchComponent
      viewer={viewer} interestListId={interestListId}
      pageString={pageString}
    />
  </LayoutContainer>);

export default InterestsPage;
